import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Project, ProjectSource } from '@captain-tasko/shared';
import { apiList, apiSingle, ApiError } from '../lib/api.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { toast } from '../components/Toast.js';
import { TAG_COLOR_PALETTE } from '@captain-tasko/shared';
import { downloadCsv } from '../lib/csv.js';
import { SortableTh } from '../components/SortableTh.js';
import { useTableSort } from '../lib/sort.js';
import { UserChip, UserSelect, useUsers } from '../components/UserPicker.js';
import { SessionRefsEditor, type SessionRefDraft } from '../components/SessionRefs.js';
import { ProjectSourcesCell, ProjectSourcesEditor } from '../components/ProjectSources.js';
// Lucide icons on the row Edit button + source-URL cell.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — installed via pnpm install
import { Pencil } from 'lucide-react';
import { CopyShortUrlButton } from '../components/CopyShortUrlButton.js';

// ── Column config (persisted to localStorage) ─────────────────────────
interface ProjectColDef { key: string; label: string; defaultVisible: boolean; sortable: boolean; align?: 'right' | 'left' }
const PROJECT_COLUMNS: ProjectColDef[] = [
  { key: 'key',                 label: 'Key',           defaultVisible: true,  sortable: true },
  { key: 'name',                label: 'Name',          defaultVisible: true,  sortable: true },
  { key: 'description',         label: 'Description',   defaultVisible: false, sortable: true },
  { key: 'ownerId',             label: 'Owner',         defaultVisible: true,  sortable: true },
  { key: 'openWorkItemCount',   label: 'Open',          defaultVisible: true,  sortable: true, align: 'right' },
  { key: 'workItemCount',       label: 'Total',         defaultVisible: true,  sortable: true, align: 'right' },
  { key: 'pastDueCount',        label: 'Past due',      defaultVisible: true,  sortable: true, align: 'right' },
  { key: 'blockingCount',       label: 'Blocking',      defaultVisible: true,  sortable: true, align: 'right' },
  { key: 'needsAttentionCount', label: 'Attention',     defaultVisible: true,  sortable: true, align: 'right' },
  { key: 'isArchived',          label: 'Status',        defaultVisible: true,  sortable: true },
  // External source URL (FR #298) — opt-in column. Renders as a small
  // external-link icon so the row stays tight; click to open in a new
  // tab. Hidden by default; operators flip it on via ⊞ Columns.
  { key: 'sourceUrl',           label: 'Source',        defaultVisible: false, sortable: false },
  { key: 'createdAt',           label: 'Created',       defaultVisible: false, sortable: true },
];
const PROJECT_COL_STORE_KEY = 'tasko.projects.cols.v1';

function loadProjectColPrefs(): Set<string> {
  if (typeof window === 'undefined') return new Set(PROJECT_COLUMNS.filter((c) => !c.defaultVisible).map((c) => c.key));
  try {
    const raw = window.localStorage.getItem(PROJECT_COL_STORE_KEY);
    if (!raw) return new Set(PROJECT_COLUMNS.filter((c) => !c.defaultVisible).map((c) => c.key));
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set(PROJECT_COLUMNS.filter((c) => !c.defaultVisible).map((c) => c.key));
  }
}

function saveProjectColPrefs(hidden: Set<string>) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(PROJECT_COL_STORE_KEY, JSON.stringify([...hidden]));
}

export function ProjectsList({ onNavigate }: { onNavigate: (path: string) => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => apiList<Project>('/projects'),
  });

  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; project: Project } | null>(null);
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(loadProjectColPrefs);
  useEffect(() => { saveProjectColPrefs(hiddenCols); }, [hiddenCols]);
  const [showColsMenu, setShowColsMenu] = useState(false);

  const visibleCols = PROJECT_COLUMNS.filter((c) => !hiddenCols.has(c.key));

  const { sorted, sort, setSort } = useTableSort(data?.data, {
    key: (p: Project) => p.key,
    name: (p) => p.name,
    description: (p) => p.description ?? '',
    ownerId: (p) => p.ownerId ?? '',
    openWorkItemCount: (p) => p.openWorkItemCount,
    workItemCount: (p) => p.workItemCount,
    pastDueCount: (p) => p.pastDueCount,
    blockingCount: (p) => p.blockingCount,
    needsAttentionCount: (p) => p.needsAttentionCount,
    isArchived: (p) => p.isArchived,
    createdAt: (p) => p.createdAt,
  }, { key: 'name', dir: 'asc' });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <div className="page-subtitle">{data?.data.length ?? 0} active</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <button
              className="btn btn--ghost"
              onClick={() => setShowColsMenu((v) => !v)}
              title="Show/hide columns"
            >
              ⊞ Columns
            </button>
            {showColsMenu && (
              <div
                role="menu"
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4,
                  background: 'var(--surface-raised)', border: '1px solid var(--border-strong)',
                  borderRadius: 'var(--radius-md)', padding: 8, minWidth: 200, zIndex: 1500,
                  boxShadow: 'var(--shadow-md)',
                }}
              >
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                  Columns
                </div>
                {PROJECT_COLUMNS.map((c) => (
                  <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={!hiddenCols.has(c.key)}
                      onChange={(e) => {
                        setHiddenCols((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.delete(c.key); else next.add(c.key);
                          return next;
                        });
                      }}
                    />
                    {c.label}
                  </label>
                ))}
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border-subtle)' }}>
                  <button className="btn btn--ghost" onClick={() => setShowColsMenu(false)} style={{ fontSize: 12, padding: '2px 8px' }}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            className="btn btn--ghost"
            aria-label="Export as CSV"
            title="Export current list as CSV"
            onClick={() => {
              const rows = data?.data ?? [];
              downloadCsv('projects.csv', [
                { header: 'Key', accessor: (r: Project) => r.key },
                { header: 'Name', accessor: (r) => r.name },
                { header: 'Description', accessor: (r) => r.description ?? '' },
                { header: 'Open items', accessor: (r) => r.openWorkItemCount },
                { header: 'Total items', accessor: (r) => r.workItemCount },
                { header: 'Past due', accessor: (r) => r.pastDueCount },
                { header: 'Blocking', accessor: (r) => r.blockingCount },
                { header: 'Needs attention', accessor: (r) => r.needsAttentionCount },
                { header: 'Archived', accessor: (r) => r.isArchived ? 'yes' : 'no' },
                { header: 'Color', accessor: (r) => r.color ?? '' },
                { header: 'Source URL', accessor: (r) => r.sourceUrl ?? '' },
                { header: 'Created', accessor: (r) => r.createdAt },
                { header: 'ID', accessor: (r) => r.id },
              ], rows);
            }}
          >
            ⤓ CSV
          </button>
          <button className="btn btn--create" onClick={() => setDialog({ mode: 'create' })}>+ New project</button>
        </div>
      </div>

      {isLoading && <SkeletonRows rows={4} />}
      {data && data.data.length === 0 && (
        <EmptyState
          title="No projects yet"
          body="Projects group work items together. Create one to start tracking."
          action={<button className="btn btn--create" onClick={() => setDialog({ mode: 'create' })}>+ New project</button>}
        />
      )}
      {data && data.data.length > 0 && (
        <table className="grid">
          <thead>
            <tr>
              {visibleCols.map((c) => (
                <SortableTh
                  key={c.key}
                  sortKey={c.key}
                  sort={sort}
                  onSortChange={setSort}
                  sortable={c.sortable}
                  style={c.align === 'right' ? { textAlign: 'right' as const } : undefined}
                >
                  {c.label}
                </SortableTh>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <ProjectRow
                key={p.id}
                project={p}
                cols={visibleCols}
                onNavigate={onNavigate}
                onEdit={() => setDialog({ mode: 'edit', project: p })}
              />
            ))}
          </tbody>
        </table>
      )}

      {dialog && (
        <ProjectDialog
          project={dialog.mode === 'edit' ? dialog.project : null}
          onCancel={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            qc.invalidateQueries({ queryKey: ['projects'] });
          }}
        />
      )}
    </>
  );
}

function ProjectRow({
  project,
  cols,
  onNavigate,
  onEdit,
}: {
  project: Project;
  cols: ProjectColDef[];
  onNavigate: (path: string) => void;
  onEdit: () => void;
}) {
  const users = useUsers();
  const owner = users.data?.data.find((u) => u.id === project.ownerId);
  const navigateToItems = `/workitems?projectId=${project.id}`;

  // Row tint precedence: past-due > blocking > needs-attention. Subtle —
  // headline still readable, but the row stands out in a long list.
  let rowTint: string | undefined;
  let rowBorderColor: string | undefined;
  if (project.pastDueCount > 0) {
    rowTint = 'color-mix(in srgb, var(--danger-primary) 5%, transparent)';
    rowBorderColor = 'var(--danger-primary)';
  } else if (project.blockingCount > 0) {
    rowTint = 'color-mix(in srgb, #B86E4D 5%, transparent)';
    rowBorderColor = '#B86E4D';
  } else if (project.needsAttentionCount > 0) {
    rowTint = 'color-mix(in srgb, #A56B00 5%, transparent)';
    rowBorderColor = '#A56B00';
  }

  function renderCell(c: ProjectColDef): React.ReactNode {
    switch (c.key) {
      case 'key':
        // Key cell → project DETAIL page (CTSK-146 — the Sessions
        // section lives there). The name cell keeps the historical
        // deep-link straight into the filtered work-item list.
        return (
          <span onClick={() => onNavigate(`/projects/${project.id}`)} style={{ cursor: 'pointer' }}>
            <span
              style={{
                display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                background: project.color ?? 'var(--accent-primary)', marginRight: 8,
              }}
            />
            <code style={{ fontFamily: 'var(--font-mono)' }}>{project.key}</code>
          </span>
        );
      case 'name':
        return (
          <strong style={{ cursor: 'pointer' }} onClick={() => onNavigate(navigateToItems)}>
            {project.name}
          </strong>
        );
      case 'description':
        return <span style={{ color: 'var(--text-muted)' }}>{project.description ?? '—'}</span>;
      case 'ownerId':
        return project.ownerId
          ? <UserChip user={owner ?? null} fallbackId={project.ownerId} />
          : <span style={{ color: 'var(--text-muted)' }}>—</span>;
      case 'openWorkItemCount':
        return <span style={{ textAlign: 'right' }}>{project.openWorkItemCount}</span>;
      case 'workItemCount':
        return <span style={{ textAlign: 'right' }}>{project.workItemCount}</span>;
      case 'pastDueCount':
        return <CountChip n={project.pastDueCount} color="var(--danger-primary)" />;
      case 'blockingCount':
        return <CountChip n={project.blockingCount} color="#B86E4D" />;
      case 'needsAttentionCount':
        return <CountChip n={project.needsAttentionCount} color="#A56B00" />;
      case 'isArchived':
        return project.isArchived
          ? <span style={{ color: 'var(--text-muted)' }}>archived</span>
          : <span style={{ color: 'var(--success-primary)' }}>active</span>;
      case 'sourceUrl':
        // CTSK-363 Slice A — default source inline + "+N" bubble for
        // the rest. No comma-splitting anywhere in this render path;
        // legacySourceUrl is the A.5 fallback for a not-yet-migrated row.
        return (
          <ProjectSourcesCell
            sources={project.sources ?? []}
            defaultSourceIndex={project.defaultSourceIndex ?? 0}
            legacySourceUrl={project.sourceUrl}
          />
        );
      case 'createdAt':
        return new Date(project.createdAt).toLocaleDateString();
      default:
        return null;
    }
  }

  return (
    <tr
      style={{
        background: rowTint,
        boxShadow: rowBorderColor ? `inset 3px 0 0 ${rowBorderColor}` : undefined,
      }}
    >
      {cols.map((c) => (
        <td key={c.key} style={c.align === 'right' ? { textAlign: 'right' } : undefined}>
          {renderCell(c)}
        </td>
      ))}
      <td>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {/* CTSK-354 — copy the /pj/:key short URL for this project. */}
          <CopyShortUrlButton shortPath={`/pj/${project.key}`} label={project.name} />
          <button
            className="btn btn--ghost"
            onClick={onEdit}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <Pencil size={13} strokeWidth={1.75} aria-hidden /> Edit
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Right-aligned count cell. Renders the number muted when zero and the
 * colour-coded chip when > 0 so the visible columns immediately tell you
 * "which projects need attention right now".
 */
function CountChip({ n, color }: { n: number; color: string }) {
  if (!n) return <span style={{ color: 'var(--text-muted)' }}>0</span>;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 24,
        padding: '1px 8px',
        borderRadius: 10,
        background: `color-mix(in srgb, ${color} 14%, transparent)`,
        color: color,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {n}
    </span>
  );
}

function ProjectDialog({
  project,
  onCancel,
  onSaved,
}: {
  project: Project | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [key, setKey] = useState(project?.key ?? '');
  const [name, setName] = useState(project?.name ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [color, setColor] = useState<string>(project?.color ?? TAG_COLOR_PALETTE[0]!);
  const [isArchived, setIsArchived] = useState(project?.isArchived ?? false);
  // Multi-source affordance (CTSK-363 Slice A) — edit-only, behind the
  // "More fields" toggle. Replaces the single sourceUrl input; the
  // server derives/dual-writes sourceUrl from sources[defaultSourceIndex]
  // so old clients stay in lockstep during the compat window.
  const [sources, setSources] = useState<ProjectSource[]>(project?.sources ?? []);
  const [defaultSourceIndex, setDefaultSourceIndex] = useState<number>(
    project?.defaultSourceIndex ?? 0,
  );
  // Session references (FR #299 / CTSK-146) — edit-only, behind "More
  // fields" like sourceUrl. Existing records round-trip verbatim (the
  // server preserves their provenance); new rows go up as {ref, label}
  // and get stamped server-side. Saving PATCHes the FULL array —
  // removing a row here is the delete surface (operator-only).
  const [sessionRefs, setSessionRefs] = useState<SessionRefDraft[]>(
    project?.sessionRefs ?? [],
  );
  // "More fields" expand. Auto-open in edit mode when the project
  // already has an advanced field set, so the operator sees their data
  // without an extra click; collapsed by default otherwise so the form
  // stays calm.
  const [showAdvanced, setShowAdvanced] = useState<boolean>(
    (project?.sources?.length ?? 0) > 0 || (project?.sessionRefs?.length ?? 0) > 0,
  );
  // Ownership transfer (CTSK-449) — EDIT-ONLY. The CTSK-446 removal of
  // the dead create-dialog picker stands: POST /projects still stamps
  // ownerId from the session. This picker is the wired-for-real edit
  // path: cap-gated render (projects:transfer_ownership), ownerId sent
  // in the PATCH body only when actually changed, 403 → toast + revert.
  const [ownerId, setOwnerId] = useState<string | null>(project?.ownerId ?? null);
  const ownerChanged = !!project && ownerId !== (project.ownerId ?? null);
  const sessionCapsQ = useQuery({
    queryKey: ['auth-session-caps'],
    queryFn: () => apiSingle<{ session: { capabilities: string[] } }>('/auth/session'),
    enabled: !!project, // create dialog never needs caps — no picker there
  });
  const canTransferOwnership =
    sessionCapsQ.data?.session.capabilities.includes('projects:transfer_ownership') ?? false;

  const save = useMutation({
    mutationFn: () => {
      if (project) {
        // edit: don't change key. sources/sessionRefs are edit-only.
        return apiSingle<Project>(`/projects/${project.id}`, {
          method: 'PATCH',
          body: {
            name,
            description: description || null,
            color,
            isArchived,
            // CTSK-449: `ownerId` is back in the PATCH body — but only
            // when the picker actually changed it (unchanged saves must
            // not trip the projects:transfer_ownership cap gate). The
            // CTSK-445/446 history: the old key was dead (schema stripped
            // it, route ignored it) and the picker was removed; this is
            // the wired-for-real edit-only restore.
            ...(ownerChanged ? { ownerId } : {}),
            // Server derives + dual-writes the legacy sourceUrl from
            // sources[defaultSourceIndex] — no need to send it here.
            sources,
            defaultSourceIndex,
            sessionRefs,
          },
        });
      }
      return apiSingle<Project>('/projects', {
        method: 'POST',
        body: {
          key: key.toUpperCase(),
          name,
          description: description || undefined,
          color,
          // CTSK-445: `ownerId` removed — not in ProjectCreateSchema (now
          // .strict(), would 400); the server stamps the owner from the
          // session and never read this key. CTSK-446 removed the dead
          // picker UI. See PATCH note above.
        },
      });
    },
    onSuccess: () => { toast.success(project ? 'Project updated' : 'Project created'); onSaved(); },
    onError: (e: Error) => {
      // CTSK-449 — a 403 on a save that carried an ownership change means
      // the caller lacks projects:transfer_ownership (the rest of PATCH is
      // gated at workitems:edit, which anyone who can open this dialog
      // holds). Friendly toast + revert the local picker state so the
      // dialog doesn't keep advertising a change the server refused.
      if (project && ownerChanged && e instanceof ApiError && e.status === 403) {
        toast.error("You don't have permission to transfer ownership");
        setOwnerId(project.ownerId ?? null);
        return;
      }
      toast.error(e.message);
    },
  });

  return (
    <div
      role="dialog"
      aria-labelledby="proj-dlg-title"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.3)', display: 'grid', placeItems: 'center', zIndex: 2000 }}
      onClick={onCancel}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface-raised)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', width: 480 }}>
        <h2 id="proj-dlg-title" style={{ margin: 0, marginBottom: 16 }}>
          {project ? `Edit project: ${project.key}` : 'New project'}
        </h2>

        <div className="field">
          <label htmlFor="proj-key">Key (2–16 uppercase letters / digits)</label>
          <input
            id="proj-key"
            value={key}
            onChange={(e) => setKey(e.target.value.toUpperCase())}
            placeholder="DRTHEO"
            disabled={!!project}
            title={project ? 'Key cannot be changed after creation' : undefined}
          />
        </div>

        <div className="field">
          <label htmlFor="proj-name">Name</label>
          <input id="proj-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="proj-desc">Description</label>
          <textarea
            id="proj-desc"
            value={description ?? ''}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            style={{ padding: 8, border: '1px solid var(--border-strong)', borderRadius: 4, fontFamily: 'inherit', resize: 'vertical' }}
          />
        </div>

        <div className="field">
          <label>Color</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {TAG_COLOR_PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Color ${c}`}
                onClick={() => setColor(c)}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 4,
                  border: `2px solid ${c === color ? 'var(--text-primary)' : 'transparent'}`,
                  background: c,
                  cursor: 'pointer',
                  padding: 0,
                }}
              />
            ))}
          </div>
        </div>

        {project && (
          <div className="field">
            <label>
              <input
                type="checkbox"
                checked={isArchived}
                onChange={(e) => setIsArchived(e.target.checked)}
              />{' '}
              Archived (hidden from default filters)
            </label>
          </div>
        )}

        {/*
          Manager / owner picker (CTSK-449) — EDIT dialog only, and only
          rendered when the caller holds projects:transfer_ownership
          (hidden, not disabled: a control most users can never use is
          noise). The create dialog deliberately has NO owner picker —
          POST /projects stamps the owner from the session (CTSK-446).
        */}
        {project && canTransferOwnership && (
          <div className="field">
            <label htmlFor="proj-owner">Manager / owner</label>
            <div id="proj-owner">
              <UserSelect value={ownerId} onChange={setOwnerId} placeholder="— Unassigned —" />
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Transfers project ownership on Save. Audited.
            </div>
          </div>
        )}

        {/*
          More fields — edit-only collapsible section [FR #298].
          Houses optional project metadata that we don't want cluttering
          the headline form. Currently: external source URL. Future
          additions (e.g. session refs [FR #299]) will live here too.
        */}
        {project && (
          <div
            style={{
              marginTop: 12,
              borderTop: '1px solid var(--border-subtle)',
              paddingTop: 8,
            }}
          >
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setShowAdvanced((v) => !v)}
              aria-expanded={showAdvanced}
              style={{
                fontSize: 12,
                color: 'var(--text-muted)',
                padding: '4px 0',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span aria-hidden style={{ fontFamily: 'var(--font-mono)' }}>
                {showAdvanced ? '▾' : '▸'}
              </span>
              More fields
            </button>

            {showAdvanced && (
              <div style={{ marginTop: 8 }}>
                <div className="field">
                  <label>Sources (CTSK-363)</label>
                  <ProjectSourcesEditor
                    sources={sources}
                    defaultSourceIndex={defaultSourceIndex}
                    onChange={({ sources: next, defaultSourceIndex: nextIdx }) => {
                      setSources(next);
                      setDefaultSourceIndex(nextIdx);
                    }}
                  />
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    External repos, docs sites, or any other link for this project. The radio picks
                    which one shows inline on the list row; the rest collapse into a "+N" bubble.
                    Changes apply on Save.
                  </div>
                </div>

                {/* Session references [FR #299 / CTSK-146]. */}
                <div className="field">
                  <label>Session references</label>
                  <SessionRefsEditor value={sessionRefs} onChange={setSessionRefs} />
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    Cowork session keys, Slack threads, or any external conversation/log that is
                    context for this project. Changes apply on Save.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button
            className="btn btn--primary"
            disabled={!name.trim() || (!project && !key.trim()) || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : (project ? 'Save changes' : 'Create project')}
          </button>
        </div>
      </div>
    </div>
  );
}
